import React, { useEffect, useState } from 'react'
import st from './Funnels.module.scss'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import ActionMenu from 'components/UI/ActionMenu/ActionMenu'
import useWindowWidth from '../utils/hooks/useWindowWidth'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { ReactComponent as DragAndDrop } from 'components/CRM/UI/icons/drag-drop-line.svg'
import { superAxiosRequest } from 'axiosApi'
import { ReactComponent as DeleteIcon } from 'components/UI/icons/delete-bin-6-line.svg'
import cogoToast from 'cogo-toast'
import Button from 'components/CRM/UI/components/Button/Button'
import { useHistory, useLocation } from 'react-router-dom'
import useLocalization from 'hooks/useLocalization'

const Funnels = () => {
  const history = useHistory()
  const windowWidth = useWindowWidth()
  const [columns, setColumns] = useState([])
  const [editingColumnId, setEditingColumnId] = useState(null)
  const [editableColumnName, setEditableColumnName] = useState('')
  const [isOpenColor, setIsOpenColor] = useState(false)
  const [colors, setColors] = useState([])

  const [funnels, setFunnels] = useState([])
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const funnelId = searchParams.get('funnel_id')

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'funnel_statuses',
    dict: {
      error: 'Your changes will be saved only after clicking the «Save» button',
      back: 'Back',
      add_column: 'Add column',
      save: 'Save',
      ensure_column_name_color:
        'Make sure that a name and color are selected for each column',
    },
  })

  const menu = [
    { name: translate.back, action: 'back' },
    { name: translate.add_column, action: 'add_column' },
    { name: translate.save, action: 'save' },
  ]
  useEffect(() => {
    let isMounted = true

    superAxiosRequest({
      link: 'integration/funnels',
      method: 'get',
      crm: true,
    })
      .then((res) => {
        setFunnels(res.data)
      })
      .catch((err) => {
        console.error(err)
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    superAxiosRequest({
      link: `integration/colors`,
      method: 'get',
      crm: true,
    })
      .then((response) => {
        setColors(response.data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    superAxiosRequest({
      link: `integration/deal_statuses`,
      method: 'get',
      crm: true,
      params: {
        funnel_id: funnelId,
      },
    })
      .then((response) => {
        const rawData = response.data
        const columnsArray = Object.keys(rawData).map((key) => ({
          ...rawData[key],
          id: key,
          position: parseInt(rawData[key].position),
        }))
        columnsArray.sort((a, b) => a.position - b.position)
        setColumns(columnsArray)
      })
      .catch(() => {})
  }, [])

  const onDragEnd = (result) => {
    if (!result.destination) return
    const items = Array.from(columns)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    const updatedColumns = items.map((item, index) => ({
      ...item,
      position: index + 1,
    }))
    setColumns(updatedColumns)
  }

  const backButton = () => {
    return history.push(`/crm/deals/?funnel_id=${funnelId}`)
  }
  //создание новой колонки, задаем дефолтный цвет и айди. Айди нужен для dnd
  const addNewColumn = () => {
    const maxPosition = columns.reduce(
      (max, column) => Math.max(max, column.position),
      0,
    )
    const newColumnId = `new-${new Date().getTime()}`
    const defaultColor = '#FFFFFF'
    const newColumn = {
      id: newColumnId,
      name: '',
      color: defaultColor,
      position: maxPosition + 1,
    }
    setColumns([...columns, newColumn])
    startEditing(newColumnId)
  }

  const startEditing = (columnId) => {
    const column = columns.find((col) => col.id === columnId)
    setEditingColumnId(columnId)
    setEditableColumnName(column ? column.name : '')
  }

  const handleNameChange = (event) => {
    setEditableColumnName(event.target.value)
    const newColumns = columns.map((column) => {
      if (column.id === editingColumnId) {
        return { ...column, name: event.target.value.trim() }
      }
      return column
    })
    setColumns(newColumns)
  }

  const updateColorInColumns = (selectedColor) => {
    const colorObj = colors.find((color) => color.body_color === selectedColor)

    const newColumns = columns.map((column) => {
      if (column.id === editingColumnId) {
        return { ...column, color: colorObj }
      }
      return column
    })
    setColumns(newColumns)
  }

  const deleteColumn = (columnId) => {
    const newColumns = columns.filter((column) => column.id !== columnId)
    setColumns(newColumns)
    cogoToast.error(translate?.error)
  }

  const saveAllChanges = () => {
    const isValid = columns.every(
      (column) =>
        column.name.trim() !== '' && column.color && column.color.body_color,
    )
    if (!isValid) {
      cogoToast.error(translate.ensure_column_name_color)
      return
    }

    const payload = columns.map((column) => {
      const columnData = {
        name: column.name,
        position: column.position,
        color_id: column.color ? column.color.id : null,
        funnel_id: funnelId,
      }
      if (!column.id.startsWith('new-')) {
        columnData.id = column.id
      }
      return columnData
    })

    superAxiosRequest(
      {
        link: `integration/change_deal_statuses`,
        method: 'post',
        crm: true,
      },
      payload,
    )
      .then(() => {})
      .catch(() => {})
  }

  const colorPicker = () => {
    setIsOpenColor(!isOpenColor)
  }

  const getInputStyle = (columnName) => {
    return {
      borderBottom: columnName && columnName.trim() ? 'none' : '1px solid red',
    }
  }

  const renderColorOptions = () => (
    <div className={st.colorBox}>
      <div className={st.colorPicker}>
        {colors.map((color) => (
          <div key={color.id} className={st.colorItem}>
            <div
              className={st.colorOption}
              style={{
                cursor: 'pointer',
                border: `1px solid ${color?.body_color !== null ? color?.body_color : '#55b1f3'}`,
              }}
              onClick={() => {
                updateColorInColumns(color.body_color)
                setIsOpenColor(false)
              }}
            >
              <div
                className={st.colorBorderCircle}
                style={{ border: `1px solid ${color?.body_color}` }}
              >
                <div
                  className={st.colorCircle}
                  style={{
                    backgroundColor:
                      color?.body_color !== null
                        ? color?.body_color
                        : '#55b1f3',
                  }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const action = (action) => {
    switch (action) {
      case 'back':
        backButton()
        break
      case 'add_column':
        addNewColumn()
        break
      case 'save':
        saveAllChanges()
        break
      default:
        break
    }
  }

  return (
    <div className={st.container}>
      <div className={st.funnelsHeader}>
        <PageHeader title={funnels?.filter((f) => f.id === +funnelId)[0]?.name}>
          {windowWidth < 768 ? (
            <ActionMenu menu={menu} action={action} />
          ) : (
            <div className={st.funnelsHeadBtn}>
              <Button
                to={`/crm/deals/?funnel_id=${funnelId}`}
                text={translate.back}
                whiteBtn
              />
              <Button
                text={translate.add_column}
                onClick={addNewColumn}
                whiteBtn
              />
              <Button text={translate.save} onClick={saveAllChanges} />
            </div>
          )}
        </PageHeader>
      </div>

      <div className={st.cont}>
        <div className={st.funnelsColumns}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='droppable-columns' direction='horizontal'>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={st.funnelsColumn}
                >
                  {columns.map((column, index) => (
                    <Draggable
                      key={column.id}
                      draggableId={column.id.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={st.funnelsColumnCard}
                        >
                          {editingColumnId === column.id &&
                          !column.is_system ? (
                            <div
                              className={st.funnelsEditColumnHeader}
                              style={{
                                borderBottom: `6px solid ${column?.color?.body_color}`,
                              }}
                            >
                              <input
                                type='text'
                                value={editableColumnName}
                                onChange={handleNameChange}
                                maxLength='35'
                                autoFocus
                                style={getInputStyle(editableColumnName)}
                              />
                              <div className={st.funnelsBtns}>
                                {!column.is_system && (
                                  <div
                                    className={st.funnelsDeleteBtn}
                                    type='button'
                                    onClick={() => deleteColumn(column.id)}
                                  >
                                    <DeleteIcon />
                                  </div>
                                )}
                                <div
                                  className={st.funnelsColor}
                                  style={{
                                    backgroundColor:
                                      column?.color?.body_color || '#1ABC97',
                                  }}
                                  onClick={colorPicker}
                                ></div>
                              </div>
                              {isOpenColor && renderColorOptions()}
                            </div>
                          ) : (
                            <div
                              className={st.funnelsColumnHeader}
                              onClick={() =>
                                startEditing(column.id, column.name)
                              }
                              style={{
                                borderBottom: `6px solid ${column?.color?.body_color}`,
                              }}
                            >
                              <span>{column.name}</span>
                              <DragAndDrop />
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </div>
  )
}

export default Funnels
